import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useCurrency, currencyData } from "../context/CurrencyContext";
import { useNotification } from "../context/NotificationContext";
import { getRecentlyViewed } from "../utils/history";
import { addAddress, getAddresses, removeAddress } from "../utils/addressBook";
import api from "../services/api";
import VerifiedSellerBadge from "../components/VerifiedSellerBadge";
import { formatPrice } from "../utils/formatPrice";
import {
  localizedCategoryName,
  localizedProductName,
} from "../utils/i18nContent";
import { formatNigerPhone, normalizeNigerPhone } from "../utils/nigerPhone";
import { 
  FiGrid, 
  FiShoppingBag, 
  FiPackage, 
  FiClock, 
  FiSettings, 
  FiEdit3, 
  FiPlus, 
  FiMail, 
  FiPhone, 
  FiGlobe, 
  FiTrash2, 
  FiAlertTriangle, 
  FiX, 
  FiCheckCircle,
  FiMapPin,
  FiActivity,
  FiShield,
  FiFileText,
} from "react-icons/fi";

const compressImageFile = (file, { maxDimension = 1600, quality = 0.8 } = {}) =>
  new Promise((resolve, reject) => {
    if (!(file instanceof File) || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const largestSide = Math.max(image.width, image.height) || 1;
        const scale = Math.min(1, maxDimension / largestSide);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        const context = canvas.getContext("2d");
        if (!context) {
          resolve(file);
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            resolve(new File([blob], file.name, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality
        );
      };
      image.onerror = () => reject(new Error("Could not process image."));
      image.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not read image."));
    reader.readAsDataURL(file);
  });

const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, completeOnboarding } = useAuth();
  const { currency, changeCurrency, rates } = useCurrency();
  const { showNotification } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = new URLSearchParams(location.search).get("tab") || "overview";

  const [myProducts, setMyProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(initialTab);
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password1: "",
    new_password2: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [buyerOrders, setBuyerOrders] = useState([]);
  const [sellerOrders, setSellerOrders] = useState([]);
  const [orderActionState, setOrderActionState] = useState({});
  const [verificationForm, setVerificationForm] = useState({
    phone_number: "",
    address: "",
    document: null,
    selfie: null,
  });
  const [showCamera, setShowCamera] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [addressForm, setAddressForm] = useState({
    label: "",
    full_name: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    region: "",
    postal_code: "",
    country: "",
    is_default: false,
  });

  const fetchProfileData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const [productsRes, ordersRes] = await Promise.all([
        api.get(`/products/?supplier=${user.id}`),
        api.get("/orders/"),
      ]);
      setMyProducts(productsRes.data.results || productsRes.data);
      setBuyerOrders(ordersRes.data.buying || []);
      setSellerOrders(ordersRes.data.selling || []);
      setRecentlyViewed(getRecentlyViewed());
      setAddresses(getAddresses());
    } catch (err) {
      console.error("Failed to fetch profile data", err);
      setError(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t, user]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  useEffect(() => {
    const nextTab = new URLSearchParams(location.search).get("tab");
    setTab(nextTab || "overview");
  }, [location.search]);

  useEffect(() => {
    const syncAddresses = () => setAddresses(getAddresses());
    window.addEventListener("chafadi-addresses-updated", syncAddresses);
    return () => window.removeEventListener("chafadi-addresses-updated", syncAddresses);
  }, []);

  const profile = useMemo(
    () => user?.supplier_profile || user?.buyer_profile || null,
    [user]
  );
  const verificationRequest = user?.verification_request || null;
  const verificationStatus = (verificationRequest?.status || "").toLowerCase();

  useEffect(() => {
    setVerificationForm((prev) => ({
      ...prev,
      phone_country_code:
        verificationRequest?.phone_country_code ||
        prev.phone_country_code ||
        "+227",
      phone_number:
        formatNigerPhone(
          verificationRequest?.phone_number ||
            user?.phone_number ||
            ""
        ) ||
        "",
    }));
  }, [user?.phone_number, verificationRequest]);

  const profileName = useMemo(() => {
    if (!user) return "";
    return (
      profile?.company_name ||
      profile?.name ||
      `${user.first_name} ${user.last_name}`.trim()
    );
  }, [profile, user]);

  const heroImage =
    profile?.logo_url ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      profileName || "Chafadi User"
    )}&background=ff7a00&color=fff&size=256&bold=true`;

  const MinimalProductItem = ({ product }) => {
    const imageUrl = product.thumbnail_url || product.logo_url || "https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/No-Image-Placeholder.svg/1665px-No-Image-Placeholder.svg.png";
    const basePrice = product.price || product.max_price || product.min_price || "0";
    const currentPrice = formatPrice(basePrice, currency, rates);
    const productName = localizedProductName(product, i18n.language);
    const categoryName = localizedCategoryName(product.category, i18n.language);

    return (
      <div className="flex gap-4 items-center py-4 border-b border-gray-50 last:border-0 group">
        <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 shrink-0 border border-gray-100">
          <img src={imageUrl} alt={productName} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold truncate group-hover:text-[#ff7a00] transition-colors">{productName}</h4>
          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">{categoryName || "Uncategorized"}</p>
          <p className="text-xs font-bold text-[#ff7a00] mt-1">{currentPrice} FCFA</p>
        </div>
      </div>
    );
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(t("profilePage.deleteConfirmation"))) return;
    try {
      await api.delete(`/products/${productId}/`);
      setMyProducts((prev) => prev.filter((product) => product.id !== productId));
      showNotification("Asset removed from grid", "warning");
    } catch (err) {
      alert(t("profilePage.deleteError"));
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordData.new_password1 !== passwordData.new_password2) {
      setPasswordError(t("profilePage.passwordsNoMatch"));
      setPasswordLoading(false);
      return;
    }

    try {
      await api.post("/auth/password/change/", passwordData);
      setPasswordSuccess(t("profilePage.passwordUpdateSuccess"));
      setPasswordData({
        old_password: "",
        new_password1: "",
        new_password2: "",
      });
    } catch (err) {
      const errorData = err.response?.data;
      setPasswordError(
        Object.values(errorData || {}).flat().join(" ") ||
          t("profilePage.passwordUpdateError")
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSettingChange = async (e) => {
    const { name, value } = e.target;
    if (name === "language") {
      const normalized = value === "fr" ? "fr" : "en";
      i18n.changeLanguage(normalized);
      localStorage.setItem("chafadi-lang", normalized);
      try {
        await api.patch("/me/language/", { language: normalized });
        const refreshedUser = await api.get("/me/");
        completeOnboarding(refreshedUser.data);
      } catch (_) {}
      showNotification(t("notifications.languageUpdated"), "success");
      return;
    }
    if (name === "currency") {
      changeCurrency(value);
    }
    const profileUrl = user?.supplier_profile ? "/profile/supplier/" : "/profile/buyer/";
    const formData = new FormData();

    formData.append(name, value);
    formData.append("first_name", user.first_name);
    formData.append("last_name", user.last_name);
    formData.append("phone_number", normalizeNigerPhone(user.phone_number || ""));
    formData.append("country", user.country?.code || user.country);

    if (user.supplier_profile) {
      formData.append("company_name", user.supplier_profile.company_name || profileName);
      formData.append("about", user.supplier_profile.about || "");
      (user.supplier_profile.industries || []).forEach((industry) => {
        formData.append("industry_ids", industry.id);
      });
    } else if (user.buyer_profile) {
      formData.append("name", user.buyer_profile.name || profileName);
      (user.buyer_profile.interested_categories || []).forEach((cat) => {
        formData.append("category_ids", cat.id);
      });
    }

    try {
      await api.patch(profileUrl, formData);
      const refreshedUser = await api.get("/me/");
      completeOnboarding(refreshedUser.data);
    } catch (err) {
      if (name === "currency") {
        changeCurrency(user.currency || "FCFA");
      }
      console.error("Failed to update settings", err.response?.data || err);
      alert(t("common.saveError") || "Failed to update settings.");
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await api.post("/me/delete-account/", {});
      setShowDeleteModal(false);
      logout();
      navigate("/login");
    } catch (err) {
      setDeleteError(
        err.response?.data?.error ||
          t("profilePage.deleteAccountError") ||
          "Failed to delete account."
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const startCamera = async () => {
    setCameraLoading(true);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access denied", err);
      showNotification("Camera access denied. Please check permissions.", "danger");
      setShowCamera(false);
    } finally {
      setCameraLoading(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const captureSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    
    // Calculate square crop for circular feel
    const size = Math.min(video.videoWidth, video.videoHeight);
    const startX = (video.videoWidth - size) / 2;
    const startY = (video.videoHeight - size) / 2;
    
    canvas.width = 600;
    canvas.height = 600;
    
    context.drawImage(video, startX, startY, size, size, 0, 0, 600, 600);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
      setVerificationForm(prev => ({ ...prev, selfie: file }));
      stopCamera();
    }, "image/jpeg", 0.9);
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!verificationForm.phone_number.trim() || !verificationForm.address.trim()) {
      showNotification("Phone and Address are required", "warning");
      return;
    }
    if (!verificationForm.document) {
      showNotification("ID Card image is required", "warning");
      return;
    }
    if (!verificationForm.selfie) {
      showNotification("A live selfie is required", "warning");
      return;
    }

    setVerificationSubmitting(true);
    try {
      const docCompressed = await compressImageFile(verificationForm.document);
      const selfieCompressed = await compressImageFile(verificationForm.selfie);

      const formData = new FormData();
      formData.append(
        "phone_number",
        normalizeNigerPhone(verificationForm.phone_number.trim())
      );
      formData.append("address", verificationForm.address.trim());
      formData.append("document", docCompressed);
      formData.append("selfie", selfieCompressed);
      
      await api.post("/verification-request/", formData);
      const refreshedUser = await api.get("/me/");
      completeOnboarding(refreshedUser.data);
      showNotification("Verification submitted successfully", "success");
    } catch (err) {
      showNotification(err.response?.data?.error || "Submission failed", "danger");
    } finally {
      setVerificationSubmitting(false);
    }
  };

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    addAddress({
      ...addressForm,
      phone: normalizeNigerPhone(addressForm.phone),
    });
    setAddresses(getAddresses());
    setAddressForm({
      label: "",
      full_name: "",
      phone: "",
      line1: "",
      line2: "",
      city: "",
      region: "",
      postal_code: "",
      country: "",
      is_default: false,
    });
    showNotification("Address synchronized", "success");
  };

  if (loading || !user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] gap-8 animate-fade-in bg-theme-bg">
        <div className="relative">
          <div className="w-24 h-24 border-4 border-chafadi-primary/20 border-t-chafadi-primary rounded-full animate-spin" />
          <FiActivity className="absolute inset-0 m-auto text-chafadi-primary animate-pulse" size={24} />
        </div>
        <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.5em]">{t("profilePage.synchronizing")}</p>
      </div>
    );
  }

  const progressOptions = [
    { value: "taken", label: t("profilePage.protocolInitialized") },
    { value: "preparing", label: t("profilePage.processingPayload") },
    { value: "out_for_delivery", label: t("profilePage.inTransit") },
    { value: "delivered", label: t("profilePage.finalDelivery") },
  ];

  const updateOrderState = (orderId, field, value) => {
    setOrderActionState((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [field]: value,
      },
    }));
  };

  const submitOrderUpdate = async (orderId, payload, successMessage) => {
    try {
      await api.patch(`/orders/${orderId}/`, payload);
      showNotification(successMessage, "success");
      fetchProfileData();
    } catch (err) {
      showNotification(t("profilePage.orderUpdateFailed"), "danger");
    }
  };

  const openReceipt = (order) => {
    const seller = order.seller || {};
    const buyer = order.buyer || {};
    const items = order.items || [];
    const address = order.address || {};
    const receiptWindow = window.open("", "_blank", "noopener,noreferrer,width=520,height=800");
    if (!receiptWindow) return;

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Chafadi Receipt #${order.id}</title>
          <style>
            body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; background: #f5f2ec; color: #171717; }
            .page { max-width: 420px; margin: 0 auto; padding: 24px; }
            .ticket { background: #fff; border-radius: 28px; overflow: hidden; border: 1px solid #ffd9b5; box-shadow: 0 16px 40px rgba(0,0,0,.08); }
            .top { padding: 20px; background: linear-gradient(180deg, #fff6ec, #fff); border-bottom: 1px dashed #ffd3a1; }
            .brand { display:flex; align-items:center; gap:12px; }
            .logo { width: 44px; height:44px; border-radius: 999px; background:#ff7a00; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:900; }
            .title { font-size: 18px; font-weight: 900; letter-spacing: .08em; }
            .muted { font-size: 11px; color:#7a6c5d; font-weight:700; }
            .content { padding: 20px; }
            .row { display:flex; justify-content:space-between; gap: 16px; margin-bottom: 10px; font-size: 12px; }
            .label { color:#8b6d4a; font-weight:800; text-transform:uppercase; letter-spacing:.08em; }
            .value { font-weight:700; text-align:right; }
            .items { margin-top: 14px; border-top: 1px dashed #ffd3a1; padding-top: 14px; }
            .item { display:flex; justify-content:space-between; gap:12px; margin-bottom: 10px; font-size: 12px; }
            .pill { display:inline-flex; padding: 4px 8px; border-radius:999px; background:#fff3e6; color:#ff7a00; font-weight:800; font-size:10px; margin-right:6px; }
            .footer { padding: 18px 20px 22px; text-align:center; font-size: 11px; color:#8b6d4a; font-weight:700; border-top: 1px dashed #ffd3a1; }
            .actions { display:flex; gap:12px; padding: 16px 0 0; }
            .btn { flex:1; border:none; border-radius: 16px; padding: 14px 16px; font-weight:800; cursor:pointer; }
            .btn.print { background:#ff7a00; color:#fff; }
            .btn.close { background:#f3f4f6; color:#111827; }
            @media print { .actions { display:none; } body { background:#fff; } .page { padding:0; } .ticket { box-shadow:none; border:none; } }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="ticket">
              <div class="top">
                <div class="brand">
                  <div class="logo">C</div>
                  <div>
                    <div class="title">CHAFADI</div>
                    <div class="muted">Marketplace Receipt</div>
                  </div>
                </div>
              </div>
              <div class="content">
                <div class="row"><span class="label">Order</span><span class="value">#${order.id}</span></div>
                <div class="row"><span class="label">Status</span><span class="value">${String(order.status || "").replaceAll("_", " ")}</span></div>
                <div class="row"><span class="label">Buyer</span><span class="value">${buyer.first_name || ""} ${buyer.last_name || ""}</span></div>
                <div class="row"><span class="label">Seller</span><span class="value">${seller.first_name || ""} ${seller.last_name || ""}</span></div>
                <div class="row"><span class="label">Phone</span><span class="value">${address.phone || buyer.phone_number || ""}</span></div>
                <div class="row"><span class="label">Tax</span><span class="value">0 CFA</span></div>
                <div class="row"><span class="label">Total</span><span class="value">${order.total_price || "0"} CFA</span></div>
                <div class="items">
                  <div class="label" style="margin-bottom:10px;">Items</div>
                  ${items.map((item) => `
                    <div class="item">
                      <div>
                        <div style="font-weight:800">${item.product_name || item.product?.name || "Product"}</div>
                        <div style="margin-top:4px">
                          ${Array.isArray(item.selected_selections) ? item.selected_selections.map((selection) => `<span class="pill">${selection.name}: ${selection.value}</span>`).join("") : ""}
                        </div>
                      </div>
                      <div style="font-weight:900; color:#ff7a00;">x${item.quantity || 1}</div>
                    </div>
                  `).join("")}
                </div>
                ${address.full_address ? `<div class="row" style="margin-top:12px;"><span class="label">Address</span><span class="value">${address.full_address}</span></div>` : ""}
              </div>
              <div class="footer">Thank you for shopping with Chafadi</div>
            </div>
            <div class="actions">
              <button class="btn print" onclick="window.print()">Download / Print</button>
              <button class="btn close" onclick="window.close()">Close</button>
            </div>
          </div>
        </body>
      </html>
    `;

    receiptWindow.document.open();
    receiptWindow.document.write(html);
    receiptWindow.document.close();
    receiptWindow.focus();
  };

  return (
    <div className="bg-[#fcfcfc] min-h-screen font-sans text-[#1d1d1f]">
      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-6 backdrop-blur-xl">
          <div className="relative w-full max-w-md aspect-square rounded-full overflow-hidden border-8 border-[#ff7a00] shadow-[0_0_80px_rgba(255,122,0,0.3)]">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted 
              className="w-full h-full object-cover scale-x-[-1]"
            />
            {/* iOS Style Circular Guide */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 border-[40px] border-black/20" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                 <div className="w-full h-full border-2 border-dashed border-white/50 rounded-full animate-[pulse_3s_infinite]" />
              </div>
            </div>
            {cameraLoading && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
              </div>
            )}
          </div>
          
          <p className="mt-12 text-white font-bold text-center max-w-xs animate-pulse tracking-wide uppercase text-[10px]">
            Position your face inside the circle and look directly at the camera.
          </p>
          
          <div className="mt-12 flex gap-6">
            <button 
              onClick={stopCamera}
              className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center text-white border border-white/20 hover:bg-white/20 transition-all"
            >
              <FiX size={24} />
            </button>
            <button 
              onClick={captureSelfie}
              disabled={cameraLoading}
              className="w-20 h-20 rounded-full bg-[#ff7a00] flex items-center justify-center text-white shadow-2xl hover:scale-110 active:scale-95 transition-all"
            >
              <div className="w-16 h-16 rounded-full border-4 border-white/30" />
            </button>
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* HEADER: Minimal and Stylish */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 border-b border-gray-100 pb-8">
          <div className="flex items-center gap-6">
            <div className="relative group">
              <div className="w-20 h-20 rounded-full bg-white border border-gray-200 p-0.5 overflow-hidden ring-2 ring-transparent group-hover:ring-[#ff7a00]/20 transition-all">
                <img
                  src={heroImage}
                  alt={profileName}
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 border-2 border-white rounded-full flex items-center justify-center text-white shadow-sm">
                <FiCheckCircle size={12} />
              </div>
            </div>
            
            <div className="space-y-1">
              <h1 className="text-2xl font-bold flex items-center gap-3">
                {profileName}
                <VerifiedSellerBadge isVerified={profile?.is_verified} size={20} />
              </h1>
              <p className="text-sm text-gray-400 font-medium">
                {profile?.about || t("profilePage.verifiedIdentity")}
              </p>
              <div className="flex gap-4 mt-2">
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  <FiPackage className="text-[#ff7a00]" /> {myProducts.length} {t("navbar.products")}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  <FiClock className="text-[#ff7a00]" /> {new Date(user.date_joining || Date.now()).getFullYear()}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/profile/edit" className="h-11 px-6 bg-white border border-gray-200 text-gray-600 text-xs font-bold rounded-lg hover:border-gray-300 transition-all flex items-center gap-2">
              <FiEdit3 size={16} /> {t("profilePage.editProfile")}
            </Link>
            <Link to="/products/add" className="h-11 px-6 bg-[#ff7a00] text-white text-xs font-bold rounded-lg hover:bg-[#e66c00] transition-all flex items-center gap-2">
              <FiPlus size={16} /> {t("navbar.addProduct")}
            </Link>
            <button onClick={logout} className="h-11 px-6 bg-white border border-gray-200 text-gray-600 text-xs font-bold rounded-lg hover:border-gray-300 transition-all flex items-center gap-2">
              <FiX size={16} /> {t("profilePage.logout")}
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* SIDEBAR NAVIGATION: Trendyol Style */}
          <aside className="lg:w-64 shrink-0">
            <nav className="flex flex-col gap-1">
              {[
                { id: "overview", label: t("profilePage.overview"), icon: FiGrid },
                { id: "products", label: t("profilePage.myListings"), icon: FiShoppingBag },
                { id: "orders", label: t("navbar.orders"), icon: FiPackage },
                { id: "activity", label: t("profilePage.myActivity"), icon: FiClock },
                { id: "verification", label: "Verification", icon: FiShield },
                { id: "settings", label: t("profilePage.accountSettings"), icon: FiSettings },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setTab(item.id)}
                  className={`flex items-center gap-4 px-5 py-4 rounded-xl text-sm font-bold transition-all ${
                    tab === item.id
                      ? "bg-[#ff7a00] text-white shadow-lg shadow-orange-500/10"
                      : "text-gray-500 hover:bg-gray-50 hover:text-[#ff7a00]"
                  }`}
                >
                  <item.icon size={18} />
                  {item.label}
                </button>
              ))}
            </nav>
            
            {/* Quick Stats list (Minimalist) */}
            <div className="mt-12 pt-8 border-t border-gray-100 space-y-6">
              <div className="space-y-4">
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">{t("profilePage.marketAnalytics") || "Analytics"}</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{t("profilePage.estimatedValue")}</span>
                    <span className="text-sm font-bold text-[#ff7a00]">
                      {myProducts[0] ? formatPrice(myProducts[0].price, currency, rates) : "---"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{t("profilePage.myActivity")}</span>
                    <span className="text-sm font-bold text-gray-800">{buyerOrders.length + sellerOrders.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* CONTENT AREA */}
          <main className="flex-1 min-w-0">
            {error && (
              <div className="mb-8 p-4 bg-orange-50 border border-orange-100 rounded-xl flex items-center gap-3 text-orange-700 text-xs font-bold uppercase tracking-wider">
                <FiAlertTriangle size={16} /> {error}
              </div>
            )}

            <div className="animate-fade-in">
              {tab === "overview" && (
                <div className="space-y-12">
                  <section>
                    <div className="flex items-center justify-between mb-8">
                      <h2 className="text-xl font-bold">{t("profilePage.myListings")}</h2>
                      <Link to="/profile?tab=products" className="text-xs font-bold text-[#ff7a00] hover:underline uppercase tracking-wider">{t("profilePage.viewAll")}</Link>
                    </div>
                    {myProducts.length > 0 ? (
                      <div className="space-y-2">
                        {myProducts.slice(0, 5).map((product) => (
                          <div key={product.id} className="group">
                             <Link to={`/products/${product.id}`} className="no-underline">
                               <MinimalProductItem product={product} />
                             </Link>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-20 text-center border-2 border-dashed border-gray-100 rounded-3xl">
                        <FiShoppingBag size={32} className="mx-auto text-gray-200 mb-4" />
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t("profilePage.noProductsListed")}</p>
                      </div>
                    )}
                  </section>

                  <section className="bg-white border border-gray-100 rounded-3xl p-8">
                    <h2 className="text-xl font-bold mb-8">{t("profilePage.contactInfo")}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {[
                        { label: t("profilePage.channel"), value: user.email, icon: FiMail },
                        { label: t("profilePage.signal"), value: formatNigerPhone(user.phone_number || "") || t("profilePage.notLinked"), icon: FiPhone },
                        { label: t("profilePage.protocol"), value: user.country || t("profilePage.nigerHub"), icon: FiGlobe },
                      ].map((field) => (
                        <div key={field.label} className="space-y-2">
                          <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            <field.icon size={12} className="text-[#ff7a00]" /> {field.label}
                          </div>
                          <div className="text-sm font-bold text-gray-800 break-all">{field.value}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {tab === "products" && (
                <div className="space-y-10">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold">{t("profilePage.myListings")}</h2>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{myProducts.length} Items</span>
                  </div>
                  
                  {myProducts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
                      {myProducts.map((product) => (
                        <div key={product.id} className="group flex flex-col justify-center">
                          <Link to={`/products/${product.id}`} className="no-underline">
                            <MinimalProductItem product={product} />
                          </Link>
                          <div className="flex gap-4 mt-2 mb-6">
                            <Link to={`/products/${product.id}/edit`} className="text-[10px] font-bold text-gray-400 hover:text-[#ff7a00] uppercase tracking-wider transition-colors">
                              {t("profilePage.edit")}
                            </Link>
                            <button
                              onClick={() => handleDeleteProduct(product.id)}
                              className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-wider transition-colors"
                            >
                              {t("profilePage.delete")}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-32 text-center">
                      <FiShoppingBag size={48} className="mx-auto text-gray-100 mb-6" />
                      <p className="text-sm font-bold text-gray-400 uppercase tracking-[0.2em] mb-8">{t("profilePage.noProductsListed")}</p>
                      <Link to="/products/add" className="inline-flex h-12 px-8 bg-[#ff7a00] text-white rounded-xl font-bold text-xs uppercase tracking-wider items-center shadow-lg shadow-orange-500/20">
                        {t("profilePage.addProduct")}
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {tab === "activity" && (
                <div className="space-y-10">
                  <h2 className="text-xl font-bold">{t("profilePage.recentlyAdded")}</h2>
                  {recentlyViewed.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
                      {recentlyViewed.map((product) => (
                        <div key={`recent-${product.id}`}>
                           <Link to={`/products/${product.id}`} className="no-underline">
                             <MinimalProductItem product={product} />
                           </Link>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-24 text-center border-2 border-dashed border-gray-100 rounded-3xl">
                       <FiClock size={32} className="mx-auto text-gray-200 mb-4" />
                       <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t("profilePage.noViewedItems")}</p>
                    </div>
                  )}
                </div>
              )}

              {tab === "orders" && (
                <div className="space-y-16">
                  {/* Buyer Orders */}
                  <div className="space-y-8">
                    <h2 className="text-xl font-bold flex items-center gap-3">
                      <FiShoppingBag className="text-[#ff7a00]" /> {t("navbar.orders")}
                    </h2>
                    <div className="space-y-4">
                      {buyerOrders.length > 0 ? (
                        buyerOrders.map((order) => {
                          const primary = order.primary_product || order.items?.[0] || {};
                          const product = primary.product || {};
                          const image = primary.product_thumbnail_url || product.thumbnail_url;
                          const name = primary.product_name || product.name || t("profilePage.redacted");
                          const price = primary.total_price || order.total_price || "0";
                          return (
                            <div key={`buyer-${order.id}`} className="bg-white border border-gray-100 rounded-2xl p-4 flex gap-6 hover:shadow-sm transition-all items-center">
                              <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 shrink-0">
                                {image ? (
                                  <img src={image} alt={name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-200">
                                    <FiPackage size={24} />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex justify-between">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">#{order.id}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-[9px] font-bold uppercase tracking-widest ${
                                    order.status === 'completed' ? 'text-emerald-500' : 'text-orange-500'
                                  }`}>
                                    {String(order.status || "").replaceAll("_", " ")}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => openReceipt(order)}
                                    className="w-8 h-8 rounded-lg bg-orange-50 border border-orange-100 text-[#ff7a00] flex items-center justify-center hover:bg-orange-100 transition-colors"
                                    title="Receipt"
                                  >
                                    <FiFileText size={14} />
                                  </button>
                                </div>
                              </div>
                                <div className="font-bold text-gray-800 truncate">{name}</div>
                                <div className="text-xs font-bold text-[#ff7a00]">{price} FCFA</div>
                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                  Qty: {primary.quantity || order.items?.[0]?.quantity || 1}
                                </div>
                                {Array.isArray(primary.selected_selections) &&
                                primary.selected_selections.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {primary.selected_selections.map((selection) => (
                                      <span
                                        key={`${order.id}-${selection.name}-${selection.value}`}
                                        className="inline-flex items-center rounded-full bg-orange-50 border border-orange-100 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[#ff7a00]"
                                      >
                                        {selection.name}: {selection.value}
                                      </span>
                                    ))}
                                  </div>
                                ) : null}
                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest flex items-center gap-1.5 mt-1">
                                  <FiActivity size={10} /> {String(order.progress_stage || "").replaceAll("_", " ")}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs font-bold text-gray-300 uppercase tracking-widest text-center py-12">{t("profilePage.zeroOrders")}</p>
                      )}
                    </div>
                  </div>

                  {/* Seller Orders */}
                  <div className="space-y-8 pt-8 border-t border-gray-100">
                    <h2 className="text-xl font-bold flex items-center gap-3">
                      <FiActivity className="text-[#ff7a00]" /> {t("profilePage.sales")}
                    </h2>
                    <div className="space-y-6">
                      {sellerOrders.length > 0 ? (
                        sellerOrders.map((order) => {
                          const actionState = orderActionState[order.id] || {};
                          return (
                            <div key={`seller-${order.id}`} className="bg-white border border-gray-100 rounded-2xl p-6 space-y-6 hover:border-[#ff7a00]/20 transition-all">
                                <div className="flex justify-between items-start">
                                <div className="space-y-1">
                                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">ORDER #{order.id}</div>
                                  <div className="font-bold text-gray-800 uppercase tracking-tight">{order.buyer?.first_name} {order.buyer?.last_name}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="h-7 px-3 flex items-center bg-orange-50 border border-orange-100 rounded-full text-[9px] font-bold uppercase tracking-widest text-[#ff7a00]">
                                    {String(order.status || "pending").replaceAll("_", " ")}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => openReceipt(order)}
                                    className="w-8 h-8 rounded-lg bg-orange-50 border border-orange-100 text-[#ff7a00] flex items-center justify-center hover:bg-orange-100 transition-colors"
                                    title="Receipt"
                                  >
                                    <FiFileText size={14} />
                                  </button>
                                </div>
                              </div>

                              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                                {order.items.map((item) => (
                                  <div key={`${order.id}-${item.product_id}`} className="flex justify-between items-start gap-3 text-xs">
                                    <div className="min-w-0">
                                      <span className="text-gray-500 font-bold uppercase truncate max-w-[200px] block">{item.product_name} <span className="text-[#ff7a00]">x{item.quantity}</span></span>
                                      {Array.isArray(item.selected_selections) &&
                                      item.selected_selections.length > 0 ? (
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                          {item.selected_selections.map((selection) => (
                                            <span
                                              key={`${order.id}-${item.product_id}-${selection.name}-${selection.value}`}
                                              className="inline-flex items-center rounded-full bg-orange-50 border border-orange-100 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[#ff7a00]"
                                            >
                                              {selection.name}: {selection.value}
                                            </span>
                                          ))}
                                        </div>
                                      ) : null}
                                    </div>
                                    <span className="font-bold text-gray-800 shrink-0">{item.total_price} FCFA</span>
                                  </div>
                                ))}
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <button 
                                  onClick={() => submitOrderUpdate(order.id, { status: "accepted" }, t("profilePage.orderAccepted"))} 
                                  className="h-10 bg-[#ff7a00] text-white text-[10px] font-bold rounded-lg uppercase tracking-wider hover:bg-[#e66c00] transition-all"
                                >
                                  {t("profilePage.acceptOrder")}
                                </button>
                                <button 
                                  onClick={() => submitOrderUpdate(order.id, { status: "completed" }, t("profilePage.orderCompleted"))} 
                                  className="h-10 bg-white border border-gray-200 text-gray-600 text-[10px] font-bold rounded-lg uppercase tracking-wider hover:bg-gray-50 transition-all"
                                >
                                  {t("profilePage.completeOrder")}
                                </button>
                              </div>

                              <div className="flex gap-2">
                                <select
                                  value={actionState.progressStage || order.progress_stage || "taken"}
                                  onChange={(e) => updateOrderState(order.id, "progressStage", e.target.value)}
                                  className="flex-1 h-10 bg-white border border-gray-200 rounded-lg px-4 text-[10px] font-bold text-gray-600 uppercase tracking-wider outline-none"
                                >
                                  {progressOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => submitOrderUpdate(order.id, { progress_stage: actionState.progressStage || order.progress_stage || "taken" }, t("profilePage.protocolStageAdvanced"))}
                                  className="h-10 px-4 bg-gray-800 text-white text-[10px] font-bold rounded-lg uppercase tracking-wider hover:bg-black transition-all"
                                >
                                  Update
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs font-bold text-gray-300 uppercase tracking-widest text-center py-12">{t("profilePage.zeroSales")}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {tab === "verification" && (
                <div className="max-w-3xl space-y-8">
                  <section className="bg-white border border-gray-100 rounded-[40px] p-8 md:p-12 space-y-10 shadow-soft">
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 rounded-[24px] bg-[#fcfcfc] border border-gray-100 flex items-center justify-center text-[#ff7a00] shadow-inner shrink-0">
                        <FiShield size={28} />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-2xl font-black tracking-tight">Identity Protocol</h2>
                        <p className="text-sm text-gray-400 font-medium leading-relaxed">
                          Complete the verification sequence to earn your badge. This process requires a valid government ID and a live biometric selfie.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className={`h-10 px-6 inline-flex items-center rounded-full text-[10px] font-black uppercase tracking-[0.3em] shadow-sm border ${
                        verificationStatus === "verified"
                          ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                          : verificationStatus === "rejected"
                          ? "bg-red-50 text-red-500 border-red-100"
                          : verificationStatus === "pending"
                          ? "bg-orange-50 text-[#ff7a00] border-orange-100 animate-pulse"
                          : "bg-gray-50 text-gray-400 border-gray-100"
                      }`}>
                        {verificationStatus || "NOT INITIALIZED"}
                      </div>
                      {verificationRequest?.rejection_reason && verificationStatus === "rejected" && (
                        <div className="flex items-center gap-2 text-red-500 bg-red-50/50 px-4 py-2 rounded-xl border border-red-100">
                          <FiAlertTriangle size={14} />
                          <span className="text-[10px] font-bold uppercase tracking-wider">{verificationRequest.rejection_reason}</span>
                        </div>
                      )}
                    </div>

                    {verificationStatus === "pending" ? (
                      <div className="py-20 text-center space-y-6">
                        <div className="w-20 h-20 rounded-full border-4 border-orange-100 border-t-[#ff7a00] animate-spin mx-auto" />
                        <div className="space-y-2">
                          <h3 className="text-xl font-bold">Verification in progress</h3>
                          <p className="text-sm text-gray-400 font-medium">We are reviewing your assets. We will notify you once successful.</p>
                        </div>
                      </div>
                    ) : verificationStatus === "verified" ? (
                      <div className="py-20 text-center space-y-6 bg-emerald-50/30 rounded-[32px] border border-emerald-100">
                        <FiCheckCircle size={64} className="mx-auto text-emerald-500" />
                        <div className="space-y-2">
                          <h3 className="text-xl font-bold text-emerald-800">Identity Verified</h3>
                          <p className="text-sm text-emerald-600/70 font-medium">Your protocol is active. You have full access to marketplace features.</p>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleVerificationSubmit} className="space-y-10">
                        <div className="space-y-8">
                          <label className="block space-y-3">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Phone Signal</span>
                            <input
                              type="tel"
                              className="w-full h-16 rounded-2xl border border-gray-100 bg-gray-50/50 px-6 font-bold text-sm outline-none focus:border-[#ff7a00] focus:ring-4 focus:ring-[#ff7a00]/5 transition-all"
                              value={verificationForm.phone_number}
                              onChange={(e) => setVerificationForm(p => ({ ...p, phone_number: formatNigerPhone(e.target.value) }))}
                              placeholder="+227 80 00 00 00"
                              required
                            />
                          </label>

                          <label className="block space-y-3">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Physical Address</span>
                            <input
                              className="w-full h-16 rounded-2xl border border-gray-100 bg-gray-50/50 px-6 font-bold text-sm outline-none focus:border-[#ff7a00] focus:ring-4 focus:ring-[#ff7a00]/5 transition-all"
                              value={verificationForm.address}
                              onChange={(e) => setVerificationForm(p => ({ ...p, address: e.target.value }))}
                              placeholder="Street, District, City"
                              required
                            />
                          </label>

                          <div className="space-y-4">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Identity Asset (ID Card)</span>
                            <label className="block relative group cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => setVerificationForm(p => ({ ...p, document: e.target.files?.[0] || null }))}
                              />
                              <div className="w-full h-40 rounded-[32px] border-2 border-dashed border-gray-100 bg-gray-50/30 group-hover:border-[#ff7a00]/30 transition-all flex flex-col items-center justify-center gap-3">
                                {verificationForm.document ? (
                                  <img 
                                    src={URL.createObjectURL(verificationForm.document)} 
                                    className="w-full h-full object-cover rounded-[30px]" 
                                    alt="ID Document Preview"
                                  />
                                ) : (
                                  <>
                                    <FiPackage className="text-gray-300 text-2xl" />
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select ID Image</span>
                                  </>
                                )}
                              </div>
                            </label>
                          </div>

                          <div className="space-y-4">
                             <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Biometric Selfie</span>
                             {!verificationForm.selfie ? (
                               <button 
                                 type="button"
                                 onClick={startCamera}
                                 className="w-full h-24 rounded-[32px] border-2 border-dashed border-[#ff7a00]/20 bg-orange-50/30 hover:bg-orange-50/50 transition-all flex items-center justify-center gap-4 group"
                               >
                                 <div className="w-12 h-12 rounded-full bg-white border border-orange-100 flex items-center justify-center text-[#ff7a00] group-hover:scale-110 transition-transform">
                                   <FiActivity />
                                 </div>
                                 <span className="text-[10px] font-black text-[#ff7a00] uppercase tracking-widest">Initialize Camera</span>
                               </button>
                             ) : (
                               <div className="relative w-40 h-40 mx-auto">
                                 <img 
                                   src={URL.createObjectURL(verificationForm.selfie)} 
                                   className="w-full h-full object-cover rounded-full border-4 border-[#ff7a00]/20 shadow-xl" 
                                   alt="Biometric Selfie Preview"
                                 />
                                 <button 
                                   type="button"
                                   onClick={startCamera}
                                   className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center text-[#ff7a00] shadow-lg hover:bg-orange-50"
                                 >
                                   <FiEdit3 size={16} />
                                 </button>
                               </div>
                             )}
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={verificationSubmitting}
                          className="w-full h-16 bg-[#ff7a00] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-orange-500/20 hover:bg-[#e66c00] active:scale-[0.98] transition-all disabled:opacity-50"
                        >
                          {verificationSubmitting ? "TRANSMITTING..." : "SUBMIT FOR REVIEW"}
                        </button>
                      </form>
                    )}
                  </section>
                </div>
              )}

              {tab === "settings" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div className="space-y-12">
                    <section className="bg-white border border-gray-100 rounded-3xl p-8 space-y-8">
                      <h2 className="text-xl font-bold">{t("profilePage.changePassword")}</h2>
                      <form onSubmit={handlePasswordSubmit} className="space-y-6">
                        {passwordError && <div className="text-[10px] font-bold text-red-500 uppercase tracking-wider">{passwordError}</div>}
                        {passwordSuccess && <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider">{passwordSuccess}</div>}
                        
                        <div className="space-y-2">
                           <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{t("profilePage.currentPassword")}</label>
                           <input type="password" name="old_password" value={passwordData.old_password} onChange={handlePasswordChange} className="w-full h-12 bg-gray-50 rounded-xl px-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 transition-all" required />
                        </div>
                        
                        <div className="space-y-4">
                           <div className="space-y-2">
                              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{t("profilePage.newPassword")}</label>
                              <input type="password" name="new_password1" value={passwordData.new_password1} onChange={handlePasswordChange} className="w-full h-12 bg-gray-50 rounded-xl px-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 transition-all" required />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{t("profilePage.confirmNewPassword")}</label>
                              <input type="password" name="new_password2" value={passwordData.new_password2} onChange={handlePasswordChange} className="w-full h-12 bg-gray-50 rounded-xl px-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 transition-all" required />
                           </div>
                        </div>
                        
                        <button type="submit" disabled={passwordLoading} className="w-full h-12 bg-[#ff7a00] text-white font-bold rounded-xl hover:bg-[#e66c00] transition-all disabled:opacity-50 text-[10px] uppercase tracking-wider">
                          {passwordLoading ? t("profilePage.updating") : t("profilePage.updatePassword")}
                        </button>
                      </form>
                    </section>

                    <section className="bg-red-50/30 border border-red-100 rounded-3xl p-8 space-y-4">
                        <h2 className="text-lg font-bold text-red-600">{t("profilePage.deleteAccount")}</h2>
                        <p className="text-xs text-red-500/70 font-medium leading-relaxed">
                          {t("profilePage.permanentlyDelete")}
                        </p>
                      <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className="h-11 px-6 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600 transition-all text-[10px] uppercase tracking-wider"
                      >
                        {t("profilePage.deleteAccount")}
                      </button>
                    </section>
                  </div>

                  <div className="space-y-12">
                    <section className="bg-white border border-gray-100 rounded-3xl p-8 space-y-8">
                      <h2 className="text-xl font-bold">{t("footer.preferencesTitle")}</h2>
                      <div className="space-y-6">
                        <div className="flex justify-between items-center py-4 border-b border-gray-50">
                           <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t("footer.currency")}</span>
                           <span className="text-sm font-bold text-gray-800">{currencyData.FCFA.name} ({currencyData.FCFA.symbol})</span>
                        </div>
                        
                        <div className="space-y-2">
                           <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{t("profilePage.recenterChannel")}</label>
                           <select
                             name="language"
                             value={["en", "fr", "ha", "tr"].includes(i18n.language) ? i18n.language : "en"}
                             onChange={handleSettingChange}
                             className="w-full h-12 bg-gray-50 rounded-xl px-4 font-bold text-xs uppercase tracking-widest outline-none border border-transparent focus:border-[#ff7a00]/20 appearance-none cursor-pointer transition-all"
                           >
                             <option value="en">{t("profilePage.englishProtocol")}</option>
                             <option value="fr">{t("profilePage.frenchTranslation")}</option>
                             <option value="ha">{t("profilePage.hausaTranslation")}</option>
                             <option value="tr">{t("profilePage.turkishTranslation")}</option>
                           </select>
                        </div>
                      </div>
                    </section>

                    <section className="bg-white border border-gray-100 rounded-3xl p-8 space-y-8">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold">{t("profilePage.distributionList") || "Addresses"}</h2>
                        <FiMapPin className="text-[#ff7a00]" />
                      </div>
                      
                      <form onSubmit={handleAddressSubmit} className="space-y-4">
                        <input placeholder={t("profilePage.labelPlaceholder")} value={addressForm.label} onChange={(e) => setAddressForm((prev) => ({ ...prev, label: e.target.value }))} className="w-full h-11 bg-gray-50 rounded-lg px-4 text-xs font-bold outline-none border border-transparent focus:border-[#ff7a00]/20" />
                        <div className="grid grid-cols-2 gap-3">
                          <input placeholder={t("profilePage.recipientSignal")} value={addressForm.full_name} onChange={(e) => setAddressForm((prev) => ({ ...prev, full_name: e.target.value }))} className="w-full h-11 bg-gray-50 rounded-lg px-4 text-xs font-bold outline-none border border-transparent focus:border-[#ff7a00]/20" required />
                          <input
                            type="tel"
                            placeholder={t("profilePage.contactPhone")}
                            value={addressForm.phone}
                            onChange={(e) => setAddressForm((prev) => ({ ...prev, phone: formatNigerPhone(e.target.value) }))}
                            className="w-full h-11 bg-gray-50 rounded-lg px-4 text-xs font-bold outline-none border border-transparent focus:border-[#ff7a00]/20"
                            required
                          />
                        </div>
                        <input placeholder={t("profilePage.addressLineAlpha")} value={addressForm.line1} onChange={(e) => setAddressForm((prev) => ({ ...prev, line1: e.target.value }))} className="w-full h-11 bg-gray-50 rounded-lg px-4 text-xs font-bold outline-none border border-transparent focus:border-[#ff7a00]/20" required />
                        <div className="grid grid-cols-2 gap-3">
                          <input placeholder={t("profilePage.city")} value={addressForm.city} onChange={(e) => setAddressForm((prev) => ({ ...prev, city: e.target.value }))} className="w-full h-11 bg-gray-50 rounded-lg px-4 text-xs font-bold outline-none border border-transparent focus:border-[#ff7a00]/20" required />
                          <input placeholder={t("profilePage.region")} value={addressForm.region} onChange={(e) => setAddressForm((prev) => ({ ...prev, region: e.target.value }))} className="w-full h-11 bg-gray-50 rounded-lg px-4 text-xs font-bold outline-none border border-transparent focus:border-[#ff7a00]/20" required />
                        </div>
                        <button type="submit" className="w-full h-12 bg-[#ff7a00] text-white text-[10px] font-bold rounded-xl uppercase tracking-wider shadow-lg shadow-orange-500/10 hover:bg-[#e66c00] transition-all">
                          {t("profilePage.addToRegistry")}
                        </button>
                      </form>

                      <div className="space-y-4 pt-4">
                        {addresses.map((address) => (
                          <div key={address.id} className="p-4 bg-gray-50 rounded-xl flex justify-between items-center group">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-800 text-xs">{address.label || address.full_name}</span>
                                {address.is_default && <span className="text-[7px] font-bold bg-[#ff7a00]/10 text-[#ff7a00] px-1.5 py-0.5 rounded uppercase">{t("profilePage.primary")}</span>}
                              </div>
                              <div className="text-[10px] text-gray-400 font-bold truncate uppercase tracking-wider mt-0.5">{address.line1}, {address.city}</div>
                            </div>
                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                              <button onClick={() => { removeAddress(address.id); setAddresses(getAddresses()); }} className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all"><FiTrash2 size={12} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

        {/* DELETE MODAL: Simplified */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDeleteModal(false)} />
            <div className="relative bg-white rounded-3xl w-full max-w-md p-10 shadow-2xl animate-zoom-in">
               <div className="flex justify-between items-start mb-6">
                  <h2 className="text-xl font-bold">{t("profilePage.confirmDeletionTitle")}</h2>
                  <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                     <FiX size={20} />
                  </button>
               </div>
               
               <p className="text-sm text-gray-500 font-medium mb-8 leading-relaxed">{t("profilePage.confirmDeletionBody1")}</p>
               
               <div className="space-y-4 mb-8">
                  <input
                    type="password"
                    placeholder="Type DELETE if you want a local confirmation step"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="w-full h-12 bg-gray-50 rounded-xl px-4 font-bold text-sm outline-none border border-transparent focus:border-red-500/20"
                  />
                  {deleteError && <div className="text-[10px] font-bold text-red-500 uppercase tracking-wider">{deleteError}</div>}
               </div>

               <div className="flex gap-3">
                 <button onClick={() => setShowDeleteModal(false)} className="h-12 flex-1 font-bold text-[10px] text-gray-400 uppercase tracking-wider rounded-xl bg-gray-50 hover:bg-gray-100 transition-all">
                   Cancel
                 </button>
                 <button 
                   onClick={handleDeleteAccount} 
                   disabled={deleteLoading}
                   className="h-12 flex-[2] bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all text-[10px] uppercase tracking-wider"
                 >
                   {deleteLoading ? "Processing..." : "Erase Account"}
                 </button>
               </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
